import { z } from "zod";
/**
 * CHALLENGES — the quick-fire brain games played on phones and the TV, and the
 * Traitors' secret tasks ("murder in plain sight").
 *
 * A challenge is described by three independent things:
 *  - `kind`: how it is answered (pick an option, type an answer, put in order…).
 *  - `category`: what it tests (logic, words, numbers…).
 *  - `percent`: roughly what share of people would get it right. This one number
 *    drives every presentation style — a ladder runs 90% down to 1%, a money
 *    tree climbs from easy to fiendish, a quick-fire round wants 60%+.
 *
 * `styles` lists presentations a challenge suits. It is a hint, not a cage: the
 * director can stage any challenge in any style its kind supports.
 */
const id = z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "ids are kebab-case");
export const CHALLENGE_CATEGORIES = [
    "logic", // patterns, sequences, deduction
    "lateral", // riddles and trick questions with a fair, satisfying answer
    "words", // anagrams, missing letters, spelling, wordplay
    "numbers", // mental arithmetic, number puzzles, estimates
    "knowledge", // general knowledge, quiz-show style
    "observation", // memory flashes and spot-the-detail
    "connections", // sorting things into hidden groups
];
export const CHALLENGE_STYLES = [
    "ladder", // "only the few": falling percentages, one wrong and you're out
    "money-tree", // four options, climbing stakes, lifelines
    "quick-fire", // short, fast, easy-ish: a buzzer round
    "showdown", // head to head between two players
    "team", // the whole table answers together
    "round-table-teaser", // a single puzzle the Steward drops before debate
];
const base = {
    id,
    category: z.enum(CHALLENGE_CATEGORIES),
    /** Estimated share of adults who'd get it right, 1–99. */
    percent: z.number().int().min(1).max(99),
    styles: z.array(z.enum(CHALLENGE_STYLES)).min(1),
    /** The question exactly as shown. Plain text; \n for line breaks. */
    prompt: z.string().min(5).max(320),
    /** Optional monospace block (number grids, letter patterns). Each string is one row. */
    grid: z.array(z.string().max(40)).max(8).optional(),
    /** Shown for `flashSec` seconds before the question, then hidden (memory challenges). */
    flash: z.string().max(400).optional(),
    flashSec: z.number().int().min(3).max(30).optional(),
    /** One or two sentences revealed afterwards: why the answer is right. */
    explain: z.string().min(5).max(300),
    /** Seconds allowed. */
    timeSec: z.number().int().min(5).max(180).default(30),
    tags: z.array(z.string()).default([]),
};
/** Pick one of 2–6 options. Covers multiple choice, true/false and odd-one-out. */
export const ChoiceChallenge = z.object({
    ...base,
    kind: z.literal("choice"),
    options: z.array(z.string().min(1).max(80)).min(2).max(6),
    /** Index into options. */
    answer: z.number().int().min(0),
    /** The tempting wrong option a Traitor (or a careless bot) would push. */
    decoy: z.number().int().min(0).optional(),
});
/** Type a short answer. Matching ignores case, punctuation, spacing and a leading "a/an/the". */
export const AnswerChallenge = z.object({
    ...base,
    kind: z.literal("answer"),
    /** Every acceptable answer; the first is the one shown in the reveal. */
    accept: z.array(z.string().min(1).max(60)).min(1),
    decoy: z.string().max(60).optional(),
});
/** Put items into the right order. Shown shuffled. */
export const OrderChallenge = z.object({
    ...base,
    kind: z.literal("order"),
    /** Items in the CORRECT order. */
    items: z.array(z.string().min(1).max(60)).min(3).max(6),
    /** What the order means, e.g. "smallest to largest". */
    orderBy: z.string().min(3).max(60),
});
/** Give a number; closest wins. */
export const EstimateChallenge = z.object({
    ...base,
    kind: z.literal("estimate"),
    answer: z.number(),
    unit: z.string().max(20).optional(),
});
/** Sort 8–16 tiles into hidden groups of four. */
export const GroupsChallenge = z.object({
    ...base,
    kind: z.literal("groups"),
    groups: z
        .array(z.object({ label: z.string().min(2).max(60), items: z.array(z.string().min(1).max(30)).length(4) }))
        .min(2)
        .max(4),
});
export const Challenge = z
    .discriminatedUnion("kind", [ChoiceChallenge, AnswerChallenge, OrderChallenge, EstimateChallenge, GroupsChallenge])
    .superRefine((c, ctx) => {
    if (c.kind === "choice") {
        if (c.answer >= c.options.length)
            ctx.addIssue({ code: "custom", path: ["answer"], message: "answer is out of range" });
        if (c.decoy !== undefined && (c.decoy >= c.options.length || c.decoy === c.answer))
            ctx.addIssue({ code: "custom", path: ["decoy"], message: "decoy must be a different, valid option" });
        if (new Set(c.options.map(normaliseAnswer)).size !== c.options.length)
            ctx.addIssue({ code: "custom", path: ["options"], message: "options must be distinct" });
        if (c.styles.includes("money-tree") && c.options.length !== 4)
            ctx.addIssue({ code: "custom", path: ["styles"], message: "money-tree questions need exactly four options" });
    }
    if (c.kind === "groups") {
        const all = c.groups.flatMap((g) => g.items.map(normaliseAnswer));
        if (new Set(all).size !== all.length)
            ctx.addIssue({ code: "custom", path: ["groups"], message: "tiles must be distinct" });
    }
    if ((c.flash === undefined) !== (c.flashSec === undefined))
        ctx.addIssue({ code: "custom", path: ["flashSec"], message: "flash and flashSec go together" });
});
/**
 * SECRET TASKS — "murder in plain sight". A Traitor is handed a task to carry out
 * on a named target without anyone noticing. The target only learns at breakfast.
 */
export const SecretTask = z.object({
    id,
    title: z.string().min(2).max(40),
    /** Shown to the Traitor. {target} is filled in. */
    task: z.string().min(10).max(220),
    /** A private tip for the Traitor on pulling it off. */
    tip: z.string().min(5).max(200),
    /** Read at breakfast. {target} is filled in. Never names the Traitor. */
    reveal: z.string().min(10).max(220),
    /** How completion is confirmed. */
    proof: z.enum(["traitor-confirms", "target-confirms"]),
    difficulty: z.enum(["easy", "medium", "hard"]),
    /** Works when guests are in different homes on a call. */
    remoteSafe: z.boolean(),
    /** Needs props from around the home. */
    objects: z.array(z.string().min(1)).default([]),
    tags: z.array(z.string()).default([]),
});
/** Lower-case, strip accents, punctuation, extra spaces and a leading article. */
export function normaliseAnswer(s) {
    return s
        .normalize("NFKD")
        .replace(/[̀-ͯ]/g, "")
        .toLowerCase()
        .replace(/&/g, " and ")
        .replace(/[^a-z0-9 ]+/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .replace(/^(the|a|an) /, "");
}
/**
 * Is this response right? Estimates are never "right" on their own — they are
 * ranked by distance — so this returns the distance for those.
 */
export function checkChallenge(c, r) {
    if (c.kind !== r.kind)
        return { correct: false };
    switch (c.kind) {
        case "choice":
            return { correct: r.option === c.answer };
        case "answer": {
            const given = normaliseAnswer(r.text);
            return { correct: given.length > 0 && c.accept.some((a) => normaliseAnswer(a) === given) };
        }
        case "order": {
            const items = r.items;
            return { correct: items.length === c.items.length && items.every((it, i) => it === c.items[i]) };
        }
        case "estimate": {
            const distance = Math.abs(r.value - c.answer);
            return { correct: distance === 0, distance };
        }
        case "groups": {
            const want = new Set(c.groups.map((g) => [...g.items].map(normaliseAnswer).sort().join("|")));
            const got = r.groups.map((g) => g.map(normaliseAnswer).sort().join("|"));
            return { correct: got.length === want.size && got.every((g) => want.has(g)) };
        }
    }
}
/** Grade for a ladder or money tree: 1–99% buckets from easy to fiendish. */
export function challengeTier(percent) {
    if (percent >= 70)
        return "gentle";
    if (percent >= 40)
        return "tricky";
    if (percent >= 15)
        return "hard";
    return "fiendish";
}
//# sourceMappingURL=challenges.js.map